/** @format */

import { useState, useEffect } from "react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  deleteDoc,
  updateDoc,
  writeBatch,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase/config";
import toast from "react-hot-toast";

const useClassroomUsers = (classroomId) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [operationLoading, setOperationLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch users associated with this classroom
  const fetchClassroomUsers = async () => {
    if (!classroomId) return;

    setLoading(true);
    setError(null);

    try {
      // Get the classroom document to access the members array
      const classroomRef = doc(db, "classrooms", classroomId);
      const classroomDoc = await getDoc(classroomRef);

      if (classroomDoc.exists()) {
        const classroomData = classroomDoc.data();
        const members = classroomData.members || [];

        console.log("Classroom data:", classroomData);
        console.log("Members array:", members);

        // Fetch full user details for each member (members can be user IDs or member objects)
        const usersWithDetails = await Promise.all(
          members.map(async (member) => {
            // Handle both old format (just user ID string) and new format (member object)
            let userId, classroomRole;
            if (typeof member === 'string') {
              userId = member;
              classroomRole = 'student'; // Default role for old format
            } else if (typeof member === 'object' && member.userId) {
              userId = member.userId;
              classroomRole = member.classroomRole || 'student';
            } else {
              console.log("Invalid member format, skipping:", member);
              return null;
            }

            console.log(`Fetching user details for userId: ${userId}, classroomRole: ${classroomRole}`);

            if (!userId) {
              console.log("No userId found, skipping member:", member);
              return null;
            }

            try {
              const userRef = doc(db, "users", userId);
              const userDoc = await getDoc(userRef);

              if (userDoc.exists()) {
                const userData = userDoc.data();
                console.log("User data from users collection:", userData);

                return {
                  userId: userId,
                  name: userData.name,
                  email: userData.email,
                  role: userData.role,
                  status: userData.status,
                  classroomRole: classroomRole,
                  addedAt: member.addedAt || null,
                  addedBy: member.addedBy || null,
                };
              } else {
                console.log(`User document ${userId} does not exist`);
                return {
                  userId: userId,
                  name: "Unknown User",
                  email: "No email",
                  role: "student",
                  status: "active",
                  classroomRole: classroomRole,
                  addedAt: member.addedAt || null,
                  addedBy: member.addedBy || null,
                };
              }
            } catch (error) {
              console.error(`Error fetching user ${userId}:`, error);
              return {
                userId: userId,
                name: "Unknown User",
                email: "No email",
                role: "student",
                status: "active",
                classroomRole: classroomRole,
                addedAt: member.addedAt || null,
                addedBy: member.addedBy || null,
              };
            }
          })
        );

        // Filter out any null values and set users
        const validUsers = usersWithDetails.filter((user) => user !== null);
        setUsers(validUsers);
        console.log("Final users array:", validUsers);
      } else {
        setUsers([]);
      }
    } catch (err) {
      console.error("Error fetching classroom users:", err);
      setError("Failed to fetch classroom users");
    } finally {
      setLoading(false);
    }
  };

  // Add user to classroom
  const addUserToClassroom = async (userData) => {
    if (!classroomId) {
      toast.error("Classroom ID is required");
      return { success: false, error: "Classroom ID is required" };
    }

    setOperationLoading(true);
    setError(null);

    try {
      // Check if user is already in the classroom
      const existingUser = users.find((u) => u.userId === userData.userId);
      if (existingUser) {
        toast.error("User is already in this classroom");
        return { success: false, error: "User already exists in classroom" };
      }

      // Get the classroom document
      const classroomRef = doc(db, "classrooms", classroomId);
      const classroomDoc = await getDoc(classroomRef);

      if (!classroomDoc.exists()) {
        toast.error("Classroom not found");
        return { success: false, error: "Classroom not found" };
      }

      // Update the members array - store member objects with user ID and classroom role
      const currentMembers = classroomDoc.data().members || [];
      const newMemberCount = currentMembers.length + 1;

      // Create member object with user ID and classroom role
      const newMember = {
        userId: userData.userId,
        classroomRole: userData.classroomRole || 'student',
        addedAt: new Date().toISOString(),
        addedBy: userData.addedBy || 'system'
      };

      console.log("Adding user to classroom:", {
        classroomId,
        userId: userData.userId,
        classroomRole: userData.classroomRole,
        currentMembers,
        newMember,
        newMemberCount,
      });

      await updateDoc(classroomRef, {
        members: [...currentMembers, newMember],
        memberCount: newMemberCount,
        updatedAt: new Date().toISOString(),
      });

      // Refresh users list
      await fetchClassroomUsers();

      toast.success("User added to classroom successfully");
      return { success: true, userId: userData.userId };
    } catch (err) {
      console.error("Error adding user to classroom:", err);
      setError("Failed to add user to classroom");
      toast.error("Failed to add user to classroom");
      return { success: false, error: err.message };
    } finally {
      setOperationLoading(false);
    }
  };

  // Remove user from classroom
  const removeUserFromClassroom = async (userId) => {
    if (!classroomId || !userId) {
      toast.error("Classroom ID and User ID are required");
      return { success: false, error: "Classroom ID and User ID are required" };
    }

    setOperationLoading(true);
    setError(null);

    try {
      // Get the classroom document
      const classroomRef = doc(db, "classrooms", classroomId);
      const classroomDoc = await getDoc(classroomRef);

      if (!classroomDoc.exists()) {
        toast.error("Classroom not found");
        return { success: false, error: "Classroom not found" };
      }

      // Update the members array by filtering out the user (handle both old and new formats)
      const currentMembers = classroomDoc.data().members || [];
      const updatedMembers = currentMembers.filter(
        (member) => {
          // Handle both old format (just user ID string) and new format (member object)
          if (typeof member === 'string') {
            return member !== userId;
          } else if (typeof member === 'object' && member.userId) {
            return member.userId !== userId;
          }
          return true; // Keep unknown format members
        }
      );
      const newMemberCount = updatedMembers.length;

      console.log("Removing user from classroom:", {
        classroomId,
        userId,
        currentMembers,
        updatedMembers,
        newMemberCount,
      });

      await updateDoc(classroomRef, {
        members: updatedMembers,
        memberCount: newMemberCount,
        updatedAt: new Date().toISOString(),
      });

      // Refresh users list
      await fetchClassroomUsers();

      toast.success("User removed from classroom successfully");
      return { success: true };
    } catch (err) {
      console.error("Error removing user from classroom:", err);
      setError("Failed to remove user from classroom");
      toast.error("Failed to remove user from classroom");
      return { success: false, error: err.message };
    } finally {
      setOperationLoading(false);
    }
  };

  // Bulk add users to classroom
  const bulkAddUsers = async (usersData) => {
    if (!classroomId || !usersData.length) {
      toast.error("Classroom ID and users data are required");
      return {
        success: false,
        error: "Classroom ID and users data are required",
      };
    }

    setOperationLoading(true);
    setError(null);

    try {
      // Get the classroom document
      const classroomRef = doc(db, "classrooms", classroomId);
      const classroomDoc = await getDoc(classroomRef);

      if (!classroomDoc.exists()) {
        toast.error("Classroom not found");
        return { success: false, error: "Classroom not found" };
      }

      // Get current members and filter out existing users
      const currentMembers = classroomDoc.data().members || [];
      const newMembers = [];

      for (const userData of usersData) {
        // Check if user is already in the classroom (handle both old format and new format)
        const existingUser = currentMembers.find(
          (member) => {
            // Handle both old format (just user ID string) and new format (member object)
            if (typeof member === 'string') {
              return member === userData.userId;
            } else if (typeof member === 'object' && member.userId) {
              return member.userId === userData.userId;
            }
            return false;
          }
        );
        if (!existingUser) {
          // Create member object with user ID and classroom role
          const newMember = {
            userId: userData.userId,
            classroomRole: userData.classroomRole || 'student',
            addedAt: new Date().toISOString(),
            addedBy: userData.addedBy || 'system'
          };
          newMembers.push(newMember);
        }
      }

      if (newMembers.length === 0) {
        toast.info("All users are already in this classroom");
        return { success: true, addedCount: 0 };
      }

      // Update the members array
      const newMemberCount = currentMembers.length + newMembers.length;

      console.log("Bulk adding users to classroom:", {
        classroomId,
        currentMembers,
        newMembers,
        newMemberCount,
      });

      await updateDoc(classroomRef, {
        members: [...currentMembers, ...newMembers],
        memberCount: newMemberCount,
        updatedAt: new Date().toISOString(),
      });

      // Refresh users list
      await fetchClassroomUsers();

      toast.success(
        `${newMembers.length} users added to classroom successfully`
      );
      return { success: true, addedCount: newMembers.length };
    } catch (err) {
      console.error("Error bulk adding users to classroom:", err);
      setError("Failed to bulk add users to classroom");
      toast.error("Failed to bulk add users to classroom");
      return { success: false, error: err.message };
    } finally {
      setOperationLoading(false);
    }
  };

  // Set up real-time listener for classroom users
  useEffect(() => {
    if (!classroomId) return;

    const classroomRef = doc(db, "classrooms", classroomId);

    const unsubscribe = onSnapshot(
      classroomRef,
      async (classroomDoc) => {
        if (classroomDoc.exists()) {
          const classroomData = classroomDoc.data();
          const members = classroomData.members || [];

          console.log("Real-time update - Classroom data:", classroomData);
          console.log("Real-time update - Members array:", members);

          // Fetch full user details for each member (members can be user IDs or member objects)
          const usersWithDetails = await Promise.all(
            members.map(async (member) => {
              // Handle both old format (just user ID string) and new format (member object)
              let userId, classroomRole;
              if (typeof member === 'string') {
                userId = member;
                classroomRole = 'student'; // Default role for old format
              } else if (typeof member === 'object' && member.userId) {
                userId = member.userId;
                classroomRole = member.classroomRole || 'student';
              } else {
                console.log("Real-time: Invalid member format, skipping:", member);
                return null;
              }

              console.log(
                `Real-time: Fetching user details for userId: ${userId}, classroomRole: ${classroomRole}`
              );

              if (!userId) {
                console.log(
                  "Real-time: No userId found, skipping member:",
                  member
                );
                return null;
              }

              try {
                const userRef = doc(db, "users", userId);
                const userDoc = await getDoc(userRef);

                if (userDoc.exists()) {
                  const userData = userDoc.data();
                  console.log(
                    "Real-time: User data from users collection:",
                    userData
                  );

                  return {
                    userId: userId,
                    name: userData.name,
                    email: userData.email,
                    role: userData.role,
                    status: userData.status,
                    classroomRole: classroomRole,
                    addedAt: member.addedAt || null,
                    addedBy: member.addedBy || null,
                  };
                } else {
                  console.log(
                    `Real-time: User document ${userId} does not exist`
                  );
                  return {
                    userId: userId,
                    name: "Unknown User",
                    email: "No email",
                    role: "student",
                    status: "active",
                    classroomRole: classroomRole,
                    addedAt: member.addedAt || null,
                    addedBy: member.addedBy || null,
                  };
                }
              } catch (error) {
                console.error(
                  `Real-time: Error fetching user ${userId}:`,
                  error
                );
                return {
                  userId: userId,
                  name: "Unknown User",
                  email: "No email",
                  role: "student",
                  status: "active",
                  classroomRole: classroomRole,
                  addedAt: member.addedAt || null,
                  addedBy: member.addedBy || null,
                };
              }
            })
          );

          // Filter out any null values and set users
          const validUsers = usersWithDetails.filter((user) => user !== null);
          setUsers(validUsers);
          setError(null);
          console.log("Real-time: Final users array:", validUsers);
        } else {
          setUsers([]);
        }
      },
      (err) => {
        console.error("Error listening to classroom users:", err);
        setError("Failed to listen to classroom users");
      }
    );

    return () => unsubscribe();
  }, [classroomId]);

  return {
    users,
    loading,
    operationLoading,
    error,
    fetchClassroomUsers,
    addUserToClassroom,
    removeUserFromClassroom,
    bulkAddUsers,
    refetch: fetchClassroomUsers,
  };
};

export default useClassroomUsers;
